import { US_COUNTRY_CODE } from "@/common/utils/us-launch.utils";

const NL_COMPACT_POSTAL_CODE = /^(\d{4})\s*([A-Z]{2})$/i;

const US_ZIP_PLUS_FOUR = /^(\d{5})[- ]?(\d{4})$/;

/**
 * WHO-5453 round 2 (F12). Display formatting for postal codes. Dutch codes are
 * written "1234 AB"; stored values often arrive as "1234ab". Only the rendered
 * text changes, the stored value and what is sent to the API stay as they are.
 *
 * WHO-4752: a US ZIP+4 is written "12345-6789". The gateway stores it that way
 * since WHO-4751; this covers an address saved before then.
 *
 * Other countries are returned unchanged.
 */
export const formatPostalCode = (
  value: string | null | undefined,
  country: string | null | undefined
) => {
  if (!value) {
    return "";
  }

  const countryCode = country?.toUpperCase();

  if (countryCode === US_COUNTRY_CODE) {
    const zip = US_ZIP_PLUS_FOUR.exec(value.trim());

    return zip ? `${zip[1]}-${zip[2]}` : value;
  }

  if (countryCode !== "NL") {
    return value;
  }

  const match = NL_COMPACT_POSTAL_CODE.exec(value.trim());

  if (!match) {
    return value;
  }

  return `${match[1]} ${match[2]?.toUpperCase()}`;
};

type CityLineAddress = {
  postal_code?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
};

/**
 * WHO-4752. The line under the street on an address card: "1015 CJ Amsterdam"
 * in Europe, "Brooklyn, NY 11201" for a US address, the order US mail uses.
 */
export const formatCityLine = (address: CityLineAddress) => {
  const postalCode = formatPostalCode(address.postal_code, address.country);

  if (address.country?.toUpperCase() === US_COUNTRY_CODE) {
    const stateAndZip = [address.state, postalCode].filter(Boolean).join(" ");

    return [address.city, stateAndZip].filter(Boolean).join(", ");
  }

  return `${postalCode} ${address.city ?? ""}`;
};
